// ═══════════════════════════════════════════════════════════════════════════
// LE TAUX FSE TEL QUE L'ONSS LE PERÇOIT — ACCESSEUR SUR L'INSTANTANÉ
//
// L'instantané est produit par `scripts/veille/moisson-onss-fse.mjs`, à partir
// du fichier des taux DmfA (XML + XSD) et de l'annexe structurée n° 27. Le
// « pourquoi » de la source est écrit en tête de `onss-fse-parse.js`.
//
// ⚠️ CE MODULE NE REND JAMAIS UN NOMBRE SEUL, et c'est le point. Trois raisons,
// toutes constatées sur les données réelles du 25/08/2026 :
//
//   1. **Une CP peut relever de plusieurs catégories d'employeur.** La CP 100
//      en reçoit huit ; leurs taux diffèrent. Élire la première serait tirer à
//      pile ou face sur la fiche de paie d'un client.
//   2. **Une catégorie porte plusieurs cotisations distinctes** : le fonds de
//      sécurité d'existence proprement dit (codes 820/830/831/832/833) et le
//      fonds de PENSION sectoriel (825/835), qui est un second pilier, pas un
//      FSE. Les additionner sous l'étiquette « FSE » serait un mensonge de
//      libellé même si le total patronal est juste.
//   3. **Certains taux sont des forfaits**, pas des pourcentages (codes
//      826/827/836/837 : tant d'euros par travailleur et par trimestre). Un
//      forfait rangé dans un champ « taux » se multiplierait par le brut.
//
// Donc : on rend des LIGNES, nommées et typées, et l'appelant tranche.
// `tauxPourcentage()` ne consent à un nombre que si la question a une seule
// réponse ; sinon il rend `null` et dit pourquoi dans `ambigu`.
// ═══════════════════════════════════════════════════════════════════════════
import INSTANTANE from '@/veille/onss-fse.json';
import { CODES_FSE, cleCp } from './onss-fse-parse.js';

/**
 * Le trimestre ONSS, écrit comme un humain l'écrit.
 *
 * ⚠️ L'instantané le porte sous la forme du fichier source — « 20263 » — et
 * cette forme partait telle quelle dans les phrases d'écran : « trimestre
 * 20263 ». Ce n'est pas une date, c'est un nom de fichier.
 */
export function trimestreLisible(brut) {
  const v = String(brut ?? '').trim();
  const m = v.match(/^(\d{4})[-/ ]?([1-4])$/);
  return m ? `${m[1]}/${m[2]}` : (v || null);
}

/** D'où vient l'instantané, et de quand il date. */
export const SOURCE_ONSS_FSE = {
  ...INSTANTANE._source,
  lu: INSTANTANE._lu,
  trimestre: INSTANTANE._trimestre,
  trimestreLisible: trimestreLisible(INSTANTANE._trimestre),
  mesure: INSTANTANE._mesure,
};

const INDEX = INSTANTANE.index || {};
const CATEGORIES = INSTANTANE.categories || {};

/** Le régime général. Les autres types sont des variantes conditionnelles
 *  (taille de l'entreprise, plan sectoriel), qui ne s'appliquent pas d'office. */
const TYPE_GENERAL = '0';

/** Les catégories d'employeur ONSS rattachées à cette CP, chacune avec ses lignes. */
export function categoriesPourCp(cp) {
  return INDEX[cleCp(cp)] || [];
}

/** La description officielle d'une catégorie d'employeur (annexe 27). */
export function descriptionCategorie(code) {
  return CATEGORIES[String(code)] || null;
}

/** Vrai si l'ONSS publie au moins une cotisation FSE pour cette CP. */
export function couverteParOnss(cp) {
  return categoriesPourCp(cp).length > 0;
}

function enrichir(ligne) {
  const meta = CODES_FSE[ligne.code] || {};
  return {
    ...ligne,
    public: meta.public || null,
    estForfait: Boolean(ligne.forfait || meta.forfait),
    libelle: meta.libelle || null,
    // Le second pilier n'est pas un fonds de sécurité d'existence : on le
    // distingue ici pour qu'aucun affichage ne les confonde.
    famille: (ligne.code === '825' || ligne.code === '835' || ligne.code === '827' || ligne.code === '837')
      ? 'pension-sectorielle'
      : 'securite-existence',
  };
}

/**
 * Les lignes de cotisation d'une CP, filtrées.
 * @param {string} cp
 * @param {{public?:'ouvrier'|'employe', categorie?:string, typeCotisation?:string}} options
 */
export function lignesPourCp(cp, options = {}) {
  const { public: pub, categorie, typeCotisation = TYPE_GENERAL } = options;
  const out = [];
  for (const bloc of categoriesPourCp(cp)) {
    if (categorie && bloc.categorie !== categorie) continue;
    for (const l of bloc.lignes) {
      if (typeCotisation !== null && l.typeCotisation !== typeCotisation) continue;
      const e = enrichir(l);
      if (pub && e.public !== pub) continue;
      out.push(e);
    }
  }
  return out;
}

/**
 * Le taux patronal en pourcentage, quand — et seulement quand — il n'y a
 * qu'une réponse possible.
 *
 * Rend toujours un objet, jamais un nombre nu :
 *   { taux, categorie, lignes, forfaits, ambigu, source }
 * `taux` vaut `null` dès que les catégories rattachées à la CP ne s'accordent
 * pas. `ambigu` porte alors la liste des valeurs concurrentes — de quoi
 * afficher « à trancher », jamais de quoi calculer en douce.
 */
const somme = (xs) => Math.round(xs.reduce((s, l) => s + (l.patronal || 0), 0) * 1e6) / 1e6;

/**
 * Ce qu'une SEULE catégorie d'employeur doit pour cette CP et ce public.
 * `null` si cette catégorie ne porte aucune ligne FSE ici.
 */
function candidatPourCategorie(cp, categorie, pub) {
  const lignes = lignesPourCp(cp, { public: pub, categorie });
  const fse = lignes.filter((l) => l.famille === 'securite-existence' && !l.estForfait);
  const pension = lignes.filter((l) => l.famille === 'pension-sectorielle' && !l.estForfait);
  const forfaits = lignes.filter((l) => l.estForfait);
  if (!fse.length && !pension.length && !forfaits.length) return null;
  // ⚠️ On arrondit APRÈS la division. `19.4 / 100` rend 0,19399999999999998 en
  // flottant, et ce chiffre finit affiché tel quel dans un écran.
  const fraction = (xs) => Math.round((somme(xs) / 100) * 1e8) / 1e8;
  return {
    categorie,
    taux: fse.length ? fraction(fse) : null,
    tauxPensionSectorielle: pension.length ? fraction(pension) : null,
    lignes: fse,
    pension,
    forfaits,
  };
}

/**
 * Les régimes CONDITIONNELS publiés pour cette CP et ce public (type ≠ 0).
 * Type 5 = « 20 travailleurs et plus » dans la construction, « 50 et plus »
 * dans l'horeca. Vide ne veut pas dire « pas de condition » : ça veut dire que
 * l'ONSS n'en publie pas d'autre.
 * @param {string|null} categorie  limite aux variantes de cette catégorie
 */
function variantesConditionnelles(cp, pub, categorie = null) {
  const out = [];
  for (const bloc of categoriesPourCp(cp)) {
    if (categorie && bloc.categorie !== categorie) continue;
    for (const l of bloc.lignes) {
      if (l.typeCotisation === TYPE_GENERAL) continue;
      const meta = CODES_FSE[l.code];
      if (!meta || meta.public !== pub) continue;
      out.push({ categorie: bloc.categorie, code: l.code, typeCotisation: l.typeCotisation, patronal: l.patronal, forfait: Boolean(l.forfait || meta.forfait) });
    }
  }
  return out;
}

/**
 * La forme canonique d'un code de catégorie ONSS : trois chiffres, zéros en
 * tête. Le dossier client peut porter « 17 », « 017 » ou 17 — l'index, lui,
 * n'a qu'une écriture. Rend `null` sur tout ce qui n'est pas une catégorie.
 */
export function normaliserCategorie(valeur) {
  const v = String(valeur ?? '').trim();
  if (!/^\d{1,3}$/.test(v)) return null;
  return v.padStart(3, '0');
}

/** Vrai si, pour ce public, les catégories rattachées à la CP ne s'accordent pas. */
export function estAmbigue(cp, options = {}) {
  const o = tauxPourcentage(cp, options);
  return Boolean(o?.ambigu);
}

/**
 * ⚖️ LE TAUX QUAND ON SAIT DE QUELLE CATÉGORIE RELÈVE L'EMPLOYEUR.
 *
 * C'est la sortie de l'ambiguïté décrite en tête de fichier — et elle ne vient
 * PAS d'un arbitrage de notre part : la catégorie d'employeur est une donnée
 * ONSS, portée par le dossier du client, et le fichier des taux répond alors
 * sans équivoque. Mesure du 25/08/2026 : **19 CP sur les 95 couvertes** ont des
 * taux qui dépendent de la catégorie, et l'écart y va jusqu'à un facteur 125
 * (CP 145 horticulture : 0,10 % en catégorie 039, 12,53 % en catégorie 194).
 *
 * ⚠️ `connue: false` VEUT DIRE « JE NE SAIS PAS », PAS « C'EST FAUX ». Le
 * rattachement catégorie → CP est lu dans la DESCRIPTION des catégories de
 * l'annexe 27, et cette description ne cite pas toujours sa commission
 * paritaire : mesuré le 25/08/2026, **143 des 156 catégories qui portent une
 * cotisation FSE** sont rattachées à au moins une CP chez nous — les 13 autres
 * (007, 032, 046, 111, 118, 121, 221, 246, 293, 421, 521, 621, 721) n'en citent
 * aucune que le parseur sache lire. Une catégorie absente d'ici peut donc être
 * parfaitement légitime. On ne l'appelle pas une contradiction, on refuse
 * seulement de s'en servir : `taux` reste `null` et l'appelant affiche « à
 * vérifier », jamais « votre dossier est faux ».
 *
 * @param {string} cp
 * @param {string|number} categorie  la catégorie ONSS de l'employeur
 * @param {{public?:'ouvrier'|'employe'}} options
 * @returns {object|null} `null` si l'ONSS ne publie rien pour cette CP.
 */
export function tauxPourCategorie(cp, categorie, options = {}) {
  const pub = options.public === 'employe' ? 'employe' : 'ouvrier';
  const blocs = categoriesPourCp(cp);
  if (!blocs.length) return null;
  const cat = normaliserCategorie(categorie);
  const attendues = blocs.map((b) => b.categorie);
  const base = { cp: cleCp(cp), public: pub, categorie: cat, categoriesDeLaCp: attendues, source: SOURCE_ONSS_FSE };
  if (!cat || !attendues.includes(cat)) {
    return { ...base, connue: false, taux: null, tauxPensionSectorielle: null, lignes: [], forfaits: [], variantes: [] };
  }
  const c = candidatPourCategorie(cp, cat, pub);
  if (!c) {
    // La catégorie existe pour cette CP, mais ne porte rien pour CE public.
    // Ce n'est pas une contradiction : une catégorie d'ouvriers ne doit rien
    // pour les employés. On rend 0, pas `null` — la question a une réponse.
    return { ...base, connue: true, taux: 0, tauxPensionSectorielle: null, lignes: [], forfaits: [], variantes: variantesConditionnelles(cp, pub, cat) };
  }
  return {
    ...base,
    connue: true,
    taux: c.taux,
    tauxPensionSectorielle: c.tauxPensionSectorielle,
    lignes: c.lignes,
    forfaits: c.forfaits,
    variantes: variantesConditionnelles(cp, pub, cat),
  };
}

export function tauxPourcentage(cp, options = {}) {
  const pub = options.public === 'employe' ? 'employe' : 'ouvrier';
  const blocs = categoriesPourCp(cp);
  if (!blocs.length) return null;

  const candidats = [];
  for (const bloc of blocs) {
    const c = candidatPourCategorie(cp, bloc.categorie, pub);
    if (c) candidats.push(c);
  }
  if (!candidats.length) return null;

  // Les autres types de cotisation : régimes CONDITIONNELS, pas des erreurs.
  const autresTypes = variantesConditionnelles(cp, pub);

  const valeurs = [...new Set(candidats.map((c) => JSON.stringify(c.taux)))];
  const accord = valeurs.length === 1;
  const retenu = candidats[0];
  return {
    cp: cleCp(cp),
    public: pub,
    taux: accord ? retenu.taux : null,
    tauxPensionSectorielle: accord ? retenu.tauxPensionSectorielle : null,
    categorie: accord ? retenu.categorie : null,
    lignes: accord ? retenu.lignes : [],
    forfaits: accord ? retenu.forfaits : [],
    ambigu: accord ? null : candidats.map((c) => ({ categorie: c.categorie, taux: c.taux })),
    // Régimes conditionnels connus pour cette CP (seuil de personnel, plan
    // sectoriel). Vide ne veut pas dire « pas de condition » : ça veut dire que
    // l'ONSS n'en publie pas d'autre pour cette catégorie.
    variantes: autresTypes,
    source: SOURCE_ONSS_FSE,
  };
}

/**
 * ⚠️ LA CLÉ SOUS LAQUELLE INTERROGER L'ONSS — LA SOUS-CP D'ABORD, LA MÈRE ENSUITE.
 *
 * L'index ONSS connaît « 149.01 » et « 332 », pas « 332.01 ». Interroger la clé
 * brute rend donc `false` sur une sous-division parfaitement couverte par sa
 * mère, et tout ce qui en dépend retombe en silence : mesuré le 25/08/2026, la
 * CP 332.01 affichait « muet — rien ne dit si ce taux vaut » là où sa mère
 * affichait « concordant avec l'ONSS », sur EXACTEMENT la même donnée héritée.
 *
 * Ce n'est pas un arbitrage — il n'y a rien à trancher : on pose la question à
 * la bonne adresse, c'est tout. La fonction vit ici pour qu'il n'y ait qu'un
 * seul endroit qui la connaisse (`fonds-se-cotisation.js` et
 * `fonds-se-statut.js` s'en servent tous les deux).
 *
 * @returns {string|null} la clé à passer aux accesseurs, ou `null`.
 */
export function cleOnssPourCp(cp) {
  const brut = cleCp(cp);
  if (!brut) return null;
  if (couverteParOnss(brut)) return brut;
  const mere = brut.replace(/[^0-9]/g, '').slice(0, 3);
  return couverteParOnss(mere) ? mere : null;
}

/** Toutes les CP pour lesquelles l'ONSS publie une cotisation FSE. */
export function cpCouvertes() {
  return Object.keys(INDEX).sort();
}

// ── Phrase d'écran, FR + NL (R8) ───────────────────────────────────────────
// Elle dit toujours QUI perçoit, SUR QUOI et DEPUIS QUEL TRIMESTRE. Une
// cotisation sectorielle affichée sans son percepteur ni sa date se lit comme
// une valeur maison — c'est précisément ce qu'on est en train de corriger.
const MOTS = {
  fr: {
    aucune: "L'ONSS ne publie aucune cotisation de fonds de sécurité d'existence pour cette commission paritaire.",
    taux: (t, cat, trim) => `L'ONSS perçoit ${t} pour le fonds de sécurité d'existence (catégorie d'employeur ${cat}, trimestre ${trim}).`,
    pension: (t) => ` S'y ajoute ${t} pour le fonds de pension sectoriel, qui est un second pilier et non un fonds de sécurité d'existence.`,
    forfait: (n) => ` ${n} cotisation${n > 1 ? 's' : ''} forfaitaire${n > 1 ? 's' : ''} s'y ajoute${n > 1 ? 'nt' : ''}, en euros par travailleur.`,
    ambigu: (n, min, max) => `Cette commission paritaire relève de ${n} catégories d'employeur aux taux différents (de ${min} à ${max}) : le taux ne peut pas être tranché sans connaître la catégorie ONSS de l'employeur. Elle se lit sur l'écran DmfA Web de l'ONSS, rubrique employeur, et se complète dans la fiche du client.`,
    horsCp: (cat, liste) => `La description ONSS de la catégorie d'employeur ${cat} ne cite pas cette commission paritaire ; celles qui la citent sont ${liste}. Le taux n'est donc pas calculable par cette voie — sans que cela prouve que le dossier soit faux, la description de l'annexe 27 ne citant pas toujours sa commission paritaire. À vérifier sur l'écran DmfA Web de l'ONSS.`,
  },
  nl: {
    aucune: 'De RSZ publiceert geen bijdrage voor een fonds voor bestaanszekerheid voor dit paritair comité.',
    taux: (t, cat, trim) => `De RSZ int ${t} voor het fonds voor bestaanszekerheid (werkgeverscategorie ${cat}, kwartaal ${trim}).`,
    pension: (t) => ` Daarbovenop komt ${t} voor het sectoraal pensioenfonds, dat een tweede pijler is en geen fonds voor bestaanszekerheid.`,
    forfait: (n) => ` Daarbij ${n > 1 ? 'komen' : 'komt'} ${n} forfaitaire bijdrage${n > 1 ? 'n' : ''}, in euro per werknemer.`,
    ambigu: (n, min, max) => `Dit paritair comité valt onder ${n} werkgeverscategorieën met verschillende voeten (van ${min} tot ${max}): de voet kan niet bepaald worden zonder de RSZ-categorie van de werkgever te kennen. Die staat op het DmfA Web-scherm van de RSZ, rubriek werkgever, en wordt in de klantenfiche aangevuld.`,
    horsCp: (cat, liste) => `De RSZ-omschrijving van werkgeverscategorie ${cat} vermeldt dit paritair comité niet; de categorieën die het wel vermelden zijn ${liste}. De voet is langs deze weg dus niet berekenbaar — zonder dat dit bewijst dat het dossier fout is, want de omschrijving in bijlage 27 vermeldt niet altijd haar paritair comité. Na te kijken op het DmfA Web-scherm van de RSZ.`,
  },
};

const pct = (f, langue) => `${(f * 100).toFixed(2).replace('.', langue === 'nl' ? ',' : ',')} %`;

/** La phrase à afficher pour une CP, dans la langue demandée. `null` si hors sujet. */
export function phraseFseOnss(cp, langue = 'fr', options = {}) {
  const m = MOTS[langue] || MOTS.fr;
  // Quand la catégorie de l'employeur est connue, il n'y a plus d'ambiguïté à
  // annoncer : l'ONSS répond une seule chose, et c'est celle-là qu'on affiche.
  const o = options.categorie != null
    ? tauxPourCategorie(cp, options.categorie, options)
    : tauxPourcentage(cp, options);
  if (!o) return couverteParOnss(cp) ? null : m.aucune;
  if (o.connue === false) {
    return m.horsCp(String(options.categorie), (o.categoriesDeLaCp || []).join(', '));
  }
  if (o.ambigu) {
    const ts = o.ambigu.map((c) => c.taux).filter((t) => typeof t === 'number');
    return m.ambigu(o.ambigu.length, pct(Math.min(...ts), langue), pct(Math.max(...ts), langue));
  }
  if (o.taux == null && !o.forfaits.length) return null;
  let phrase = o.taux == null ? '' : m.taux(pct(o.taux, langue), o.categorie, SOURCE_ONSS_FSE.trimestreLisible);
  if (o.tauxPensionSectorielle) phrase += m.pension(pct(o.tauxPensionSectorielle, langue));
  if (o.forfaits.length) phrase += m.forfait(o.forfaits.length);
  return phrase.trim();
}
